// =============================================================================
//  api_subscriptions.ts
//
//  Standalone subscription module for AiAcceleratorCoach. Houses:
//    - Subscription-related TypeScript types
//    - SubscriptionService class (Stripe checkout, free tier, cancellation,
//      updates, billing history, webhooks, cohort enrollment)
//    - Plan and status constants
//    - The shared `subscriptionService` instance
//
//  Depends on three helpers exported by ./api:
//    - API_BASE_URL     (Vite-driven base URL)
//    - getAuthHeaders() (adds Authorization bearer token)
//    - handleResponse() (parses JSON / throws on non-2xx)
//
//  Note (bug fix from api.js): the original JS exported `subscribeFreeTier`
//  but SubscriptionPlans.js called `subscribeFreetier`. This module
//  standardizes on `subscribeFreeTier` (capital T). Update any callers.
// =============================================================================

import { API_BASE_URL, getAuthHeaders, handleResponse } from './api';
import type { APIResponse } from '../types';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

// The catalog's vocabulary is 'annual' | 'monthly' (stripe/catalog.json). 'annually' is
// retained ONLY because subscriptions created before the catalog landed have it stored in
// their Stripe metadata and in our records — a Subscription read back from the backend can
// still carry it. New requests must send the catalog's word.
export type BillingInterval = 'annual' | 'monthly' | 'annually';

export type SubscriptionStatusType =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing';

export interface CheckoutSessionResponse {
  sessionId: string;
  url?: string;
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  status: SubscriptionStatusType;
  billingInterval: BillingInterval;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPlanRecord {
  id: string;
  name: string;
  description: string;
  annualPriceId: string;
  annualPrice: number;
  features: string[];
}

export interface BillingHistoryItem {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'failed' | 'pending' | 'refunded';
  invoiceUrl?: string;
  description?: string;
}

export interface CohortEnrollment {
  id: string;
  title: string;
}

export interface StripeWebhookEvent {
  signature: string;
  data: unknown;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

export const SUBSCRIPTION_PLANS = {
  FREE: 'free_tier',
  BASIC: 'basic_tier',
  PREMIUM: 'premium_tier',
  ELITE: 'elite_tier',
  // AiAcceleratorCoach tiers
  AI_SPARK: 'accelerator_spark',
  AI_BUILDER: 'accelerator_builder',
  AI_FOUNDER: 'accelerator_founder',
  // Elementary tiers
  ELEM_SPARK: 'elem_spark',
  ELEM_BUILDER: 'elem_builder',
  ELEM_FOUNDER: 'elem_founder',
} as const;

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  TRIALING: 'trialing',
} as const;

export type SubscriptionPlanId =
  (typeof SUBSCRIPTION_PLANS)[keyof typeof SUBSCRIPTION_PLANS];

export type ModuleId = 'one_line_pitch' | 'short_description' | 'long_description'
  | 'problem_statement' | 'solution_description' | 'why_now'
  | 'target_market' | 'market_size' | 'go_to_market_strategy'
  | 'traction_summary' | 'unit_economics' | 'team_description'
  | 'use_of_funds' | 'vision_statement' | 'three_year_plan'
  | 'founder_identity' | 'business_model_canvas' | 'competitive_positioning'
  | 'metrics_framework' | 'team_strategy' | 'funding_strategy'
  | 'milestone_roadmap' | 'beachhead_market' | 'product_showcase'
  | 'founder_presence';

export const NARRATIVE_MODULES_ORDERED: ModuleId[] = [
  'one_line_pitch', 'short_description', 'long_description',
  'problem_statement', 'solution_description', 'why_now',
  'target_market', 'market_size', 'go_to_market_strategy',
  'traction_summary', 'unit_economics', 'team_description',
  'use_of_funds', 'vision_statement', 'three_year_plan',
  'founder_identity', 'business_model_canvas', 'competitive_positioning',
  'metrics_framework', 'team_strategy', 'funding_strategy',
  'milestone_roadmap', 'beachhead_market', 'product_showcase',
  'founder_presence',
];

export const ALL_MODULES: ModuleId[] = [...NARRATIVE_MODULES_ORDERED];

export const MODULE_ACCESS_BY_PLAN: Record<string, ModuleId[]> = {
  [SUBSCRIPTION_PLANS.AI_SPARK]:   NARRATIVE_MODULES_ORDERED.slice(0, 3),
  [SUBSCRIPTION_PLANS.AI_BUILDER]: NARRATIVE_MODULES_ORDERED.slice(0, 8),
  [SUBSCRIPTION_PLANS.AI_FOUNDER]: [...NARRATIVE_MODULES_ORDERED], // all 25 modules
};

export const PROFILE_LIMITS_BY_PLAN: Record<string, number> = {
  [SUBSCRIPTION_PLANS.AI_SPARK]: 1,
  [SUBSCRIPTION_PLANS.AI_BUILDER]: 3,
  [SUBSCRIPTION_PLANS.AI_FOUNDER]: 10,
};

export const NARRATIVE_MODULE_TO_LEXICON_FIELD: Record<ModuleId, string> = {
  one_line_pitch: 'oneLinePitch',
  short_description: 'shortDescription',
  long_description: 'longDescription',
  problem_statement: 'problemStatement',
  solution_description: 'solutionDescription',
  why_now: 'whyNow',
  target_market: 'targetMarket',
  market_size: 'marketSize',
  go_to_market_strategy: 'goToMarketStrategy',
  traction_summary: 'tractionSummary',
  unit_economics: 'unitEconomics',
  team_description: 'teamDescription',
  use_of_funds: 'useOfFunds',
  vision_statement: 'visionStatement',
  three_year_plan: 'threeYearPlan',
  founder_identity: 'founderName',
  business_model_canvas: 'businessModel',
  competitive_positioning: 'competitiveAdvantage',
  metrics_framework: 'currentMRR',
  team_strategy: 'teamSize',
  funding_strategy: 'fundingHistory',
  milestone_roadmap: 'keyMilestones',
  beachhead_market: 'beachheadMarket',
  product_showcase: 'productStatus',
  founder_presence: 'companyWebsite',
};

// Reverse mapping: Lexicon field name -> ModuleId
export const LEXICON_FIELD_TO_NARRATIVE_MODULE: Record<string, ModuleId> =
  Object.fromEntries(
    Object.entries(NARRATIVE_MODULE_TO_LEXICON_FIELD).map(([k, v]) => [v, k as ModuleId])
  ) as Record<string, ModuleId>;

// Additional field → module mappings for multi-field narrative modules
LEXICON_FIELD_TO_NARRATIVE_MODULE['founderLocation'] = 'founder_identity';
LEXICON_FIELD_TO_NARRATIVE_MODULE['foundedDate'] = 'founder_identity';
LEXICON_FIELD_TO_NARRATIVE_MODULE['incorporationStatus'] = 'founder_identity';
LEXICON_FIELD_TO_NARRATIVE_MODULE['tags'] = 'founder_identity';
LEXICON_FIELD_TO_NARRATIVE_MODULE['industries'] = 'founder_identity';
LEXICON_FIELD_TO_NARRATIVE_MODULE['revenueModel'] = 'business_model_canvas';
LEXICON_FIELD_TO_NARRATIVE_MODULE['pricingModel'] = 'business_model_canvas';
LEXICON_FIELD_TO_NARRATIVE_MODULE['revenueStreams'] = 'business_model_canvas';
LEXICON_FIELD_TO_NARRATIVE_MODULE['competitors'] = 'competitive_positioning';
LEXICON_FIELD_TO_NARRATIVE_MODULE['differentiators'] = 'competitive_positioning';
LEXICON_FIELD_TO_NARRATIVE_MODULE['targetCustomerProfile'] = 'competitive_positioning';
LEXICON_FIELD_TO_NARRATIVE_MODULE['currentARR'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['monthlyGrowthRate'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['totalUsers'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['payingCustomers'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['customerAcquisitionCost'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['lifetimeValue'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['burnRate'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['runway'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['marketSizeNumber'] = 'metrics_framework';
LEXICON_FIELD_TO_NARRATIVE_MODULE['coFounders'] = 'team_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['keyHires'] = 'team_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['whyThisTeam'] = 'team_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['totalRaised'] = 'funding_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['currentRound'] = 'funding_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['targetRaise'] = 'funding_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['previousInvestors'] = 'funding_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['whatYouNeed'] = 'funding_strategy';
LEXICON_FIELD_TO_NARRATIVE_MODULE['next12MonthGoals'] = 'milestone_roadmap';
LEXICON_FIELD_TO_NARRATIVE_MODULE['keyFeatures'] = 'product_showcase';
LEXICON_FIELD_TO_NARRATIVE_MODULE['technologyStack'] = 'product_showcase';
LEXICON_FIELD_TO_NARRATIVE_MODULE['demoVideoUrl'] = 'founder_presence';
LEXICON_FIELD_TO_NARRATIVE_MODULE['productDemoUrl'] = 'founder_presence';
LEXICON_FIELD_TO_NARRATIVE_MODULE['visibility'] = 'founder_presence';

// ModuleId -> route slug (used by lightbulb navigation)
export const NARRATIVE_MODULE_TO_ROUTE_SLUG: Record<ModuleId, string> = {
  one_line_pitch: 'one-line-pitch',
  short_description: 'short-description',
  long_description: 'long-description',
  problem_statement: 'problem-statement',
  solution_description: 'solution-description',
  why_now: 'why-now',
  target_market: 'target-market',
  market_size: 'market-size',
  go_to_market_strategy: 'go-to-market-strategy',
  traction_summary: 'traction-summary',
  unit_economics: 'unit-economics',
  team_description: 'team-description',
  use_of_funds: 'use-of-funds',
  vision_statement: 'vision-statement',
  three_year_plan: 'three-year-plan',
  founder_identity: 'founder-identity',
  business_model_canvas: 'business-model-canvas',
  competitive_positioning: 'competitive-positioning',
  metrics_framework: 'metrics-framework',
  team_strategy: 'team-strategy',
  funding_strategy: 'funding-strategy',
  milestone_roadmap: 'milestone-roadmap',
  beachhead_market: 'beachhead-market',
  product_showcase: 'product-showcase',
  founder_presence: 'founder-presence',
};

export type ElementaryItemId = 'allowance_savings' | 'charitable_giving' | 'goal_setting' | 'lemonade_stand' | 'wants_needs';

export const ALL_ELEMENTARY_ITEMS: ElementaryItemId[] = [
  'allowance_savings',
  'charitable_giving',
  'goal_setting',
  'lemonade_stand',
  'wants_needs',
];

export const ELEMENTARY_ACCESS_BY_PLAN: Record<string, ElementaryItemId[]> = {
  [SUBSCRIPTION_PLANS.ELEM_SPARK]:   ['allowance_savings'],
  [SUBSCRIPTION_PLANS.ELEM_BUILDER]: ['allowance_savings', 'charitable_giving', 'goal_setting'],
  [SUBSCRIPTION_PLANS.ELEM_FOUNDER]: ALL_ELEMENTARY_ITEMS,
};

// -----------------------------------------------------------------------------
// Service
// -----------------------------------------------------------------------------

export class SubscriptionService {
  /**
   * Create a Stripe Checkout Session and return a sessionId for client-side
   * redirect via stripe.redirectToCheckout({ sessionId }).
   */
  async createCheckoutSession(
    lookupKey: string,
    userId: string,
    userEmail: string,
    planId: string,
    billingInterval: BillingInterval,
    locale: string = 'en',
    metadata: Record<string, unknown> = {}
  ): Promise<APIResponse<CheckoutSessionResponse>> {
    try {
      const { quantity, custom_amount_cents, billing_interval, installments, ...restMetadata } = metadata as Record<string, unknown>;
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          // A catalog lookup_key, NOT a price ID. Keys are stable across environments;
          // price IDs are not, which is why the backend resolves this inside its own
          // account (PLAN-StripePricingMultiSite.md §4.6).
          lookupKey,
          userId,
          userEmail,
          planId,
          billingInterval: (billing_interval as string) || billingInterval,
          locale,
          metadata: restMetadata,
          quantity: quantity || 1,
          custom_amount_cents: custom_amount_cents || 0,
          installments: installments || 1,
        }),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Checkout session created',
      };
    } catch (error) {
      console.error('Create checkout session error:', error);
      throw error;
    }
  }

  /**
   * Subscribe a user to the free tier without going through Stripe.
   */
  async subscribeFreeTier(
    userId: string,
    planId: string
  ): Promise<APIResponse<Subscription>> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/free-tier`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId, planId }),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Free tier subscription created',
      };
    } catch (error) {
      console.error('Free tier subscription error:', error);
      throw error;
    }
  }

  async getCurrentSubscription(
    userId: string
  ): Promise<APIResponse<Subscription | null>> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${userId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription retrieved',
      };
    } catch (error) {
      console.error('Get subscription error:', error);
      throw error;
    }
  }

  async cancelSubscription(
    subscriptionId: string
  ): Promise<APIResponse<Subscription>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/${subscriptionId}/cancel`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription canceled',
      };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  }

  async updateSubscription(
    subscriptionId: string,
    updateData: Partial<Subscription>
  ): Promise<APIResponse<Subscription>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/${subscriptionId}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData),
        }
      );
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription updated',
      };
    } catch (error) {
      console.error('Update subscription error:', error);
      throw error;
    }
  }

  async getSubscriptionPlans(): Promise<APIResponse<SubscriptionPlanRecord[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Subscription plans retrieved',
      };
    } catch (error) {
      console.error('Get subscription plans error:', error);
      throw error;
    }
  }

  async getBillingHistory(
    userId: string
  ): Promise<APIResponse<BillingHistoryItem[]>> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/${userId}/billing-history`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Billing history retrieved',
      };
    } catch (error) {
      console.error('Get billing history error:', error);
      throw error;
    }
  }

  async handleWebhookEvent(
    event: StripeWebhookEvent
  ): Promise<APIResponse<unknown>> {
    try {
      const response = await fetch(`${API_BASE_URL}/webhooks/stripe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Stripe-Signature': event.signature,
        },
        body: JSON.stringify(event.data),
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data,
        message: 'Webhook processed',
      };
    } catch (error) {
      console.error('Webhook handling error:', error);
      throw error;
    }
  }

  /**
   * Enroll a user in a free cohort. Returns true on success.
   */
  async enrollInCohort(cohort: CohortEnrollment): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/cohort-enrollment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          cohort_id: cohort.id,
          cohort_name: cohort.title,
          price: 0,
          payment_id: 'free_enrollment',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to enroll in free cohort');
      }
      return true;
    } catch (error) {
      console.error('Error enrolling in free cohort:', error);
      throw error;
    }
  }
}

// -----------------------------------------------------------------------------
// Shared instance
// -----------------------------------------------------------------------------

export const subscriptionService = new SubscriptionService();

export default subscriptionService;
